import { useState, useEffect } from 'react';
import { X, UserPlus, Users, Trash2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const ASSIGNMENT_ROLES = [
  { value: 'OWNER', label: 'Capture Manager', color: 'bg-emerald-100 text-emerald-800' },
  { value: 'WRITER', label: 'Writer', color: 'bg-blue-100 text-blue-800' },
  { value: 'REVIEWER', label: 'Reviewer', color: 'bg-purple-100 text-purple-800' }
];

export default function AssignTeamModal({ isOpen, onClose, pursuit, onAssignmentChange }) {
  const { authToken } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [assignments, setAssignments] = useState([]);
  const [availableMembers, setAvailableMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // Form state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedRole, setSelectedRole] = useState('REVIEWER');
  const [notes, setNotes] = useState('');
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (isOpen && pursuit?.id) {
      fetchAssignments();
      fetchAvailableMembers();
    }
  }, [isOpen, pursuit?.id]);

  const fetchAssignments = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/assignments/pursuit/${pursuit.id}`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssignments(data.items || []);
      }
    } catch (err) {
      console.error('Error fetching assignments:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableMembers = async () => {
    try {
      const response = await fetch(`${API_URL}/api/assignments/available-team-members`, {
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAvailableMembers(data);
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
    }
  };

  const handleAddAssignment = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setAdding(true);
    setError(null);
    
    try {
      const response = await fetch(`${API_URL}/api/assignments/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pursuit_id: pursuit.id,
          user_id: selectedUser,
          role: selectedRole,
          notes: notes || null
        })
      });
      
      if (response.ok) {
        const newAssignment = await response.json();
        setAssignments(prev => [...prev, newAssignment]);
        setSelectedUser('');
        setNotes('');
        onAssignmentChange?.();
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Failed to add assignment');
      }
    } catch (err) {
      setError('Failed to add team member');
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveAssignment = async (assignmentId) => {
    try {
      const response = await fetch(`${API_URL}/api/assignments/${assignmentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        setAssignments(prev => prev.filter(a => a.id !== assignmentId));
        onAssignmentChange?.();
      }
    } catch (err) {
      console.error('Error removing assignment:', err);
    }
  };

  const handleRoleChange = async (assignmentId, newRole) => {
    try {
      const response = await fetch(`${API_URL}/api/assignments/${assignmentId}?role=${newRole}`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${authToken}` }
      });
      
      if (response.ok) {
        setAssignments(prev => 
          prev.map(a => a.id === assignmentId ? { ...a, role: newRole } : a)
        );
        onAssignmentChange?.();
      }
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  // Filter out already assigned users
  const unassignedMembers = availableMembers.filter(
    member => !assignments.some(a => a.user_id === member.id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div className="fixed inset-0 bg-black/50" onClick={onClose} />
        
        {/* Modal */}
        <div className="relative bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Assign Team</h2>
                <p className="text-sm text-gray-500 truncate max-w-md">
                  {pursuit?.opportunity_name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          
          {/* Content */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {/* Add Team Member Form */}
            <form onSubmit={handleAddAssignment} className="mb-6">
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Team Member
                  </label>
                  <select
                    value={selectedUser}
                    onChange={(e) => setSelectedUser(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  >
                    <option value="">Select a team member...</option>
                    {unassignedMembers.map(member => (
                      <option key={member.id} value={member.id}>
                        {member.first_name} {member.last_name} ({member.role})
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="w-40">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Role
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => setSelectedRole(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  >
                    {ASSIGNMENT_ROLES.map(role => (
                      <option key={role.value} value={role.value}>
                        {role.label}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={!selectedUser || adding}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <UserPlus className="w-4 h-4" />
                    {adding ? 'Adding...' : 'Add'}
                  </button>
                </div>
              </div>
              
              {error && (
                <div className="mt-2 flex items-center gap-2 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </div>
              )}
            </form>
            
            {/* Current Assignments */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Current Team ({assignments.length})
              </h3>
              
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
                </div>
              ) : assignments.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
                  <p>No team members assigned yet</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {assignments.map(assignment => {
                    const roleInfo = ASSIGNMENT_ROLES.find(r => r.value === assignment.role);
                    return (
                      <div
                        key={assignment.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center text-sm font-medium text-gray-600">
                            {assignment.user_name?.split(' ').map(n => n[0]).join('').toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {assignment.user_name || assignment.user_email}
                            </p>
                            <p className="text-xs text-gray-500">
                              {assignment.user_email}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <select
                            value={assignment.role}
                            onChange={(e) => handleRoleChange(assignment.id, e.target.value)}
                            className={`text-xs px-2 py-1 rounded-full border-0 font-medium ${roleInfo?.color || 'bg-gray-100'}`}
                          >
                            {ASSIGNMENT_ROLES.map(role => (
                              <option key={role.value} value={role.value}>
                                {role.label}
                              </option>
                            ))}
                          </select>
                          
                          <button
                            onClick={() => handleRemoveAssignment(assignment.id)}
                            className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove from team"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
